import type { ClientMsg, RoomSummary, ServerMsg } from '@shared/protocol';

export async function createRoom(name?: string, holes = 3): Promise<{ id: string }> {
  const res = await fetch('/api/rooms', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name, holes }) });
  if (!res.ok) throw new Error(`create room failed: ${res.status}`);
  return (await res.json()) as { id: string };
}

export async function listRooms(): Promise<RoomSummary[]> {
  const res = await fetch('/api/rooms');
  return res.ok ? ((await res.json()) as RoomSummary[]) : [];
}

/** The room socket. Reconnects with backoff and re-joins with the saved identity, so a dropped
 *  connection on a headset is a pause, not a lost seat. */
export class Net {
  private ws: WebSocket | null = null;
  private closedByUs = false;
  private attempts = 0;
  private keepalive: number | null = null;
  constructor(
    private roomId: string,
    private name: string,
    private identity: () => { id: string; token: string } | null,
    private onMessage: (msg: ServerMsg) => void,
    private onStatus: (s: 'connecting' | 'open' | 'closed') => void,
  ) {}

  connect() {
    this.closedByUs = false;
    this.onStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/room/${this.roomId}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.onStatus('open');
      const me = this.identity();
      this.send({ t: 'join', name: this.name, playerId: me?.id, token: me?.token });
      this.keepalive = window.setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25_000);
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data !== 'string' || ev.data === 'pong') return;
      try { this.onMessage(JSON.parse(ev.data) as ServerMsg); } catch { /* ignore a bad frame */ }
    };
    ws.onclose = () => {
      if (this.keepalive) { clearInterval(this.keepalive); this.keepalive = null; }
      this.onStatus('closed');
      if (this.closedByUs) return;
      const delay = Math.min(15_000, 500 * 2 ** this.attempts++);
      setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => { /* onclose follows */ };
  }

  send(msg: ClientMsg) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close() {
    this.closedByUs = true;
    this.ws?.close();
  }
}
